import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Phone, Mail, Building2, CheckCircle, ArrowRight } from 'lucide-react';

const CompanyUAE = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    'Global Sourcing & Procurement',
    'Oil & Gas Equipment Supply',
    'Industrial Machinery & Equipment',
    'Electronics & Components Trading',
    'Agricultural Commodities Export',
    'International Trade Facilitation',
    'Supply Chain Management',
    'Customs Clearance Support',
  ];

  return (
    <>
      <Helmet>
        <title>Eaglesway General Trading FZ-LLC | UAE | Official Company Profile</title>
        <meta 
          name="description" 
          content="Eaglesway General Trading FZ-LLC - Leading international trading company in Ras Al Khaimah, UAE. Global sourcing, oil & gas supply, industrial equipment, and commodities trading." 
        />
      </Helmet>

      <div className="min-h-screen bg-black text-white pt-20">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <div className="w-48 h-48 bg-gradient-to-br from-[#F5E6D3] via-[#E8D4B8] to-[#D4AF37] rounded-xl border-2 border-[#D4AF37] p-8 flex items-center justify-center shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_premium-sourcing-7/artifacts/nupvqz6o_WhatsApp%20Image%202025-12-25%20at%202.59.55%20PM.jpeg"
                  alt="Eaglesway General Trading"
                  className="max-w-full max-h-full object-contain"
                  style={{ mixBlendMode: 'multiply', filter: 'contrast(1.3) brightness(1.1)' }}
                />
              </div>
            </div>

            {/* Company Name */}
            <div className="text-center mb-6">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
                Eaglesway <span className="text-[#D4AF37]">General Trading</span> FZ-LLC
              </h1>
              <div className="inline-block bg-[#D4AF37] text-black text-sm font-semibold px-6 py-2 rounded-full">
                Head Office – Trading Division
              </div>
            </div>

            {/* Company Description */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 p-8 mb-8">
              <p className="text-gray-300 text-lg leading-relaxed">
                Eaglesway General Trading FZ-LLC is the flagship trading division of Eaglesway Group, operating from Ras Al Khaimah, United Arab Emirates. The company specializes in international sourcing, procurement, and distribution of oil & gas equipment, industrial machinery, electronics, and agricultural commodities. With established networks across global markets, Eaglesway General Trading connects suppliers and buyers worldwide, ensuring quality, competitive pricing, and reliable logistics. The company serves clients in energy, construction, manufacturing, and agriculture sectors with comprehensive trade solutions.
              </p>
            </Card>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-gradient-to-b from-gray-900 to-black border border-gray-800 p-6">
                <MapPin className="w-8 h-8 text-[#D4AF37] mb-3" />
                <h3 className="text-white font-semibold mb-2">Address</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Ras Al Khaimah, United Arab Emirates
                </p>
              </Card>

              <Card className="bg-gradient-to-b from-gray-900 to-black border border-gray-800 p-6">
                <Phone className="w-8 h-8 text-[#D4AF37] mb-3" />
                <h3 className="text-white font-semibold mb-2">Phone</h3>
                <a href="tel:+971525712721" className="text-[#D4AF37] hover:underline text-sm">
                  +971 52 571 2721
                </a>
              </Card>

              <Card className="bg-gradient-to-b from-gray-900 to-black border border-gray-800 p-6">
                <Mail className="w-8 h-8 text-[#D4AF37] mb-3" />
                <h3 className="text-white font-semibold mb-2">Email</h3>
                <a href="mailto:sales.eaglesway@gmail.com" className="text-[#D4AF37] hover:underline text-sm break-all">
                  sales.eaglesway@gmail.com
                </a>
              </Card>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Services & <span className="text-[#D4AF37]">Capabilities</span>
              </h2>
              <p className="text-gray-400 text-lg">
                Comprehensive trading and procurement solutions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service, index) => (
                <Card key={index} className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 hover:border-[#D4AF37] transition-all duration-300 p-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-[#D4AF37] mr-4 flex-shrink-0 mt-1" />
                    <span className="text-gray-300 text-lg">{service}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Start Trading with <span className="text-[#D4AF37]">Eaglesway UAE</span>
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Contact our team for sourcing inquiries, quotations, and partnership opportunities
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/quote')}
                className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-10 py-7 text-lg transition-all duration-300"
              >
                Request a Quote
                <ArrowRight className="ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                variant="outline"
                className="border-2 border-[#D4AF37] text-[#D4AF37] bg-transparent hover:bg-[#D4AF37] hover:text-black font-semibold px-10 py-7 text-lg transition-all duration-300"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CompanyUAE;